import { serviceRequest } from './service-request';
import type { LLMResponse } from '@cognitoforms/types/server-types/core/services/forms/a-i/llm-response';
import type { ChatContext } from '@cognitoforms/types/server-types/model/chat-context';
import type { LLMToolCall } from '@cognitoforms/types/server-types/core/services/forms/a-i/llm-tool-call';
import type { LlmPageMeta } from '@cognitoforms/types/server-types/core/services/forms/a-i/llm-page-meta';
import type { AppPage } from '@cognitoforms/types/server-types/model/app-page';

export function promptSupportChat(conversationId: string, prompt: string, context: ChatContext[], appPage: AppPage, url?: string): Promise<void> {
	return serviceRequest(`svc/support-agent/chats/${encodeURIComponent(conversationId)}`, {
		prompt,
		context,
		appPage,
		url
	}, {
		method: 'POST'
	});
}

export function streamSupportChatResponse(
	conversationId: string,
	appPageMeta: LlmPageMeta,
	onChunk: (chunk: LLMResponse) => Promise<void>,
	onError?: (error: Event) => void,
	onComplete?: () => void
): EventSource {
	const appPageMetaParam = encodeURIComponent(JSON.stringify(appPageMeta));
	const url = `/svc/support-agent/chats/${encodeURIComponent(conversationId)}/stream?pageMeta=${appPageMetaParam}`;
	const eventSource = new EventSource(url);

	eventSource.onmessage = async (event) => {
		if (event.data === '[DONE]') {
			eventSource.close();
			onComplete?.();
			return;
		}

		try {
			const chunk = JSON.parse(event.data) as LLMResponse;
			await onChunk(chunk);
		}
		catch (e) {
			console.error('Failed to parse SSE data:', event.data, e);
		}
	};

	eventSource.onerror = (error) => {
		eventSource.close();
		onError?.(error);
	};

	return eventSource;
}

export function completeToolCall(conversationId: string, toolCall: LLMToolCall, output: string) {
	return serviceRequest(`svc/support-agent/chats/${encodeURIComponent(conversationId)}/tool/${toolCall.Id}`, {
		toolName: toolCall.ToolName,
		output
	});
}

export type CreateTicketArgs = {
	ticketType: string;
	summary: string;
	formId: string;
	email?: string;
}

export function createTicket(conversationId: string, args: CreateTicketArgs) {
	return serviceRequest(`svc/support-agent/chats/${encodeURIComponent(conversationId)}/ticket`, args,
		{
			method: 'POST'
		});
}

export function generateSummary(conversationId: string, prompt: string, response: string): Promise<string> {
	return serviceRequest(`svc/support-agent/chats/${encodeURIComponent(conversationId)}/generate-summary`, {
		prompt,
		response
	}, {
		method: 'POST'
	});
}